import type { TransferTarget, TransferType } from 'Modules/softphone/types/transfer.types';

/**
 * Pure function that determines if a transfer option should be disabled.
 *
 * - `blind`: disabled only when no valid destination is selected
 * - `warm`: disabled when no valid destination OR phone number has fewer than 3 digits
 * - `voicemail`: disabled when target is not a directory contact with voicemail active,
 *   OR when the selected phone number is not the Office/extension number.
 *   This matches the legacy behavior where voicemail transfer is only available
 *   for the Office entry (extension), not for other phone numbers like Business or Mobile.
 *
 * @returns `true` if the option should be disabled, `false` if enabled
 */
export function getTransferOptionDisabled(
  option: TransferType,
  target: TransferTarget | null,
  selectedPhoneNumber: string | null
): boolean {
  // No destination selected → all disabled
  if (!target || !selectedPhoneNumber) {
    return true;
  }

  switch (option) {
    case 'blind':
      // Blind is always available for valid destinations
      return false;

    case 'warm': {
      // Warm requires a valid phone number with at least 2 digits
      const digitCount = selectedPhoneNumber.replace(/[^0-9]/g, '').length;
      return digitCount < 2;
    }

    case 'voicemail': {
      if (target.type !== 'contact') {
        return true;
      }
      if (target.contactType !== 'directory') {
        return true;
      }
      // Explicitly disabled when voicemailInboxId is '0'
      if (target.voicemailInboxId === '0') {
        return true;
      }
      // Voicemail is only available for the Office/extension number.
      // Other numbers (Business, Mobile, etc.) cannot receive voicemail transfers.
      const officeEntry = target.phoneNumbers.find((p) => p.label === 'Office');

      if (!officeEntry) {
        return true;
      }
      return selectedPhoneNumber !== officeEntry.number;
    }

    default:
      return true;
  }
}
